<template>
  <div
    class="base-pull-to-refresh"
    :class="{
      'is-shown': distance > 0 || refreshing,
      'is-armed': armed,
      'is-refreshing': refreshing,
      'is-dragging': dragging,
    }"
    :style="position"
    aria-hidden="true"
  >
    <BaseIcon
      name="rotate-cw"
      class="base-pull-to-refresh__icon"
      :style="{ transform: `rotate(${distance * 3}deg)` }"
    />
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';

// How far the finger travels, in px, before the pull counts, how far the indicator must come
// down to refresh, and how far it can go.
const SLOP = 8;
const THRESHOLD = 64;
const MAX = 96;
const SIZE = 40;

// Pull down at the top of the page to refresh, on touch screens. The indicator slides over the
// content (the content doesn't move, so nothing pinned to the screen moves with it) and spins
// until `pending` says the refresh is done. The page's refresh button remains: the gesture is a
// shortcut for it, not the only way.
export default {
  name: 'BasePullToRefresh',
  components: { BaseIcon },
  props: {
    pending: {
      type: Boolean,
      default: false,
    },
    // The element the page scrolls in.
    scroller: {
      type: String,
      default: '.base-layout__content',
    },
    // What's pinned at the top of it (the navigation bar): the indicator comes out from under it.
    topOffset: {
      type: Number,
      default: 56,
    },
  },
  data() {
    return {
      distance: 0,
      dragging: false,
      refreshing: false,
      origin: null,
      scrollerBox: null,
    };
  },
  computed: {
    armed() {
      return this.distance >= THRESHOLD;
    },
    position() {
      if (!this.scrollerBox) return {};
      const shown = this.refreshing ? THRESHOLD : this.distance;
      return {
        top: `${this.scrollerBox.top + this.topOffset + shown - SIZE}px`,
        left: `${this.scrollerBox.left + this.scrollerBox.width / 2 - SIZE / 2}px`,
      };
    },
  },
  watch: {
    pending(pending) {
      if (!pending) this.refreshing = false;
    },
  },
  methods: {
    // The inbox and Explore share the scroller and stay alive in the background: only the page
    // on screen listens, or a pull on one would also refresh the other.
    listen() {
      const el = this.$el && this.$el.closest(this.scroller);
      if (!el || this.el === el) return;
      this.el = el;
      el.addEventListener('touchstart', this.onTouchStart, { passive: true });
      // Not passive: while pulling, the browser must not bounce the page as well.
      el.addEventListener('touchmove', this.onTouchMove, { passive: false });
      el.addEventListener('touchend', this.onTouchEnd);
      el.addEventListener('touchcancel', this.onTouchEnd);
    },
    stopListening() {
      if (!this.el) return;
      this.el.removeEventListener('touchstart', this.onTouchStart);
      this.el.removeEventListener('touchmove', this.onTouchMove);
      this.el.removeEventListener('touchend', this.onTouchEnd);
      this.el.removeEventListener('touchcancel', this.onTouchEnd);
      this.el = null;
    },
    onTouchStart(event) {
      this.origin = null;
      if (this.refreshing || event.touches.length !== 1 || this.el.scrollTop > 0) return;
      const touch = event.touches[0];
      this.origin = { x: touch.clientX, y: touch.clientY, pulling: false };
      this.scrollerBox = this.el.getBoundingClientRect();
    },
    onTouchMove(event) {
      if (!this.origin) return;
      const touch = event.touches[0];
      const dx = touch.clientX - this.origin.x;
      const dy = touch.clientY - this.origin.y;
      if (!this.origin.pulling) {
        if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
        // Sideways (a tab strip), upwards (scrolling the list) or no longer at the top: not ours.
        if (Math.abs(dx) > dy || this.el.scrollTop > 0) {
          this.origin = null;
          return;
        }
        this.origin.pulling = true;
        this.dragging = true;
      }
      if (event.cancelable) event.preventDefault();
      // Half the finger's travel: the indicator lags behind, as a pull should feel.
      this.distance = Math.max(0, Math.min(MAX, (dy - SLOP) / 2));
    },
    onTouchEnd() {
      if (this.origin && this.origin.pulling && this.armed) this.refresh();
      this.origin = null;
      this.dragging = false;
      this.distance = 0;
    },
    refresh() {
      this.refreshing = true;
      this.$emit('refresh');
      // A refresh that didn't start (nothing to search yet) leaves `pending` alone.
      this.$nextTick(() => {
        if (!this.pending) this.refreshing = false;
      });
    },
  },
  mounted() {
    this.listen();
  },
  activated() {
    this.listen();
  },
  deactivated() {
    this.stopListening();
  },
  beforeDestroy() {
    this.stopListening();
  },
};
</script>

<style lang="scss">
.base-pull-to-refresh {
  position: fixed;
  // Over the page header pinned below the navigation bar, under the navigation bar itself.
  z-index: $z-index-header + 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: $radius-full;
  background: $color-white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  color: $color-slate-500;
  opacity: 0;
  pointer-events: none;
  transition: opacity 150ms ease, top 200ms ease;

  // The finger moves it: no easing.
  &.is-dragging {
    transition: none;
  }

  &.is-shown {
    opacity: 1;
  }

  &.is-armed,
  &.is-refreshing {
    color: $color-accent;
  }

  &__icon {
    width: 20px;
    height: 20px;
  }

  &.is-refreshing &__icon {
    animation: base-pull-to-refresh-spin 800ms linear infinite;
  }
}

@keyframes base-pull-to-refresh-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
